import axiosInstance from './axiosCreator';
import { BlockAnswers, BlockType, BlockKeyValueMap } from '../types/Study';
import {
  blockAnswerUpdated,
  blocksLoaded,
  blockUpdated,
} from '../slices/blocks/blocksSlice';
import { store } from '../store/store';
import { todaySessionReceived } from '../slices/todaySession/todaySessionSlice';
import { setVignettes } from '../slices/vignettes/vignettesSlice';

export const getBlockById = async (blockId: string) => {
  try {
    const response = await axiosInstance.get(`/blocks/${blockId}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getBlockByIdForReview = async (blockId: string) => {
  try {
    const response = await axiosInstance.get(`/blocks/${blockId}/review`);
    if (
      response.data &&
      response.data.studySession &&
      response.data.block &&
      response.data.vignettes
    ) {
      const studySession = response.data.studySession;
      store.dispatch(
        todaySessionReceived({
          ...studySession,
          isReview: true,
          activeBlockId: blockId,
        })
      );
      store.dispatch(blocksLoaded([response.data.block]));
      store.dispatch(setVignettes(response.data.vignettes));
    }
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const updateBlock = async ({
  blockId,
  type,
  answers,
  flags,
  notes,
  answerCrossouts,
  date,
  duration,
  isStarted,
  isSubmitted,
  correctCount,
  incorrectCount,
  omittedCount,
  vignetteCount,
  timeSetting,
  ord,
  questionHighlights,
  isRecalculated,
}: {
  blockId: string;
  type?: BlockType;
  answers?: BlockAnswers;
  flags?: BlockKeyValueMap<boolean>;
  notes?: BlockKeyValueMap<string>;
  answerCrossouts?: BlockKeyValueMap<boolean[]>;
  date?: Date;
  duration?: number;
  isStarted?: boolean;
  isSubmitted?: boolean;
  correctCount?: number;
  incorrectCount?: number;
  omittedCount?: number;
  vignetteCount?: number;
  timeSetting?: string;
  ord?: number;
  questionHighlights?: BlockKeyValueMap<string>;
  isRecalculated?: boolean;
}) => {
  try {
    const response = await axiosInstance.put(`/blocks/${blockId}`, {
      ...(type !== undefined && { type }),
      ...(answers !== undefined && { answers }),
      ...(flags !== undefined && { flags }),
      ...(notes !== undefined && { notes }),
      ...(answerCrossouts !== undefined && { answerCrossouts }),
      ...(date !== undefined && { date }),
      ...(duration !== undefined && { duration }),
      ...(isStarted !== undefined && { isStarted }),
      ...(isSubmitted !== undefined && { isSubmitted }),
      ...(correctCount !== undefined && { correctCount }),
      ...(incorrectCount !== undefined && { incorrectCount }),
      ...(omittedCount !== undefined && { omittedCount }),
      ...(vignetteCount !== undefined && { vignetteCount }),
      ...(timeSetting !== undefined && { timeSetting }),
      ...(ord !== undefined && { ord }),
      ...(questionHighlights !== undefined && { questionHighlights }),
      ...(isRecalculated !== undefined && { isRecalculated }),
    });
    if (response.data) {
      store.dispatch(blockUpdated(response.data));
    }
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const addBlockResponse = async (
  blockId: string,
  vignetteId: string,
  answer: string,
  correctAnswer: string,
  submitted: boolean,
  vignetteContentId: string,
  secondAnswer?: string,
  secondSubmitted?: boolean
) => {
  try {
    const answerData = {
      vignetteContentId,
      answer,
      correctAnswer,
      submitted,
      secondAnswer,
      secondSubmitted,
    };
    // Optimistic update of block answer
    store.dispatch(blockAnswerUpdated({ blockId, vignetteId, answerData }));
    const response = await axiosInstance.put(`/blocks/${blockId}/response`, {
      vignetteId,
      answer,
      correctAnswer,
      submitted,
      vignetteContentId,
      secondAnswer,
      secondSubmitted,
    });
    if (response.data) {
      store.dispatch(blockUpdated(response.data));
    }
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const toggleBlockFlag = async (blockId: string, vignetteId: string) => {
  try {
    const response = await axiosInstance.put(`/blocks/${blockId}/flags`, {
      vignetteId,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const updateBlockNotes = async (
  blockId: string,
  vignetteId: string,
  notes: string
) => {
  try {
    const response = await axiosInstance.put(`/blocks/${blockId}/notes`, {
      vignetteId,
      notes,
    });
    if (response.data) {
      store.dispatch(blockUpdated(response.data));
    }
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

interface SubmitBlockScoreObject {
  correctCount: number;
  incorrectCount: number;
  omittedCount: number;
}

export const submitBlock = async (
  blockId: string,
  score: SubmitBlockScoreObject,
  isTimedMode: boolean,
  isDemo: boolean
) => {
  try {
    const response = await axiosInstance.post(`/blocks/${blockId}/submit`, {
      score,
      isTimedMode,
      isDemo,
    });
    if (response.data) {
      store.dispatch(blockUpdated(response.data));
    }
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const updateBlockCrossouts = async (
  blockId: string,
  vignetteId: string,
  crossouts: boolean[]
) => {
  try {
    const response = await axiosInstance.put(`/blocks/${blockId}/crossouts`, {
      vignetteId,
      crossouts,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const updateBlockHighlights = async (
  blockId: string,
  vignetteId: string,
  highlights: string
) => {
  try {
    const response = await axiosInstance.put(`/blocks/${blockId}/highlights`, {
      vignetteId,
      highlights,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
